import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Users, Mail, Clock, Target, Lock } from 'lucide-react';
import { collection, addDoc, getDocs, deleteDoc, doc, setDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from './firebase';

const UserForm = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({
    uid: '',
    email: '',
    password: '',
    tradingHours: '',
    trigger: '',
    userName: ''
  });

  // Fetch users from Firebase on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const userCollection = collection(db, 'users');
        const userSnapshot = await getDocs(userCollection);
        const userList = userSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        }));
        setUsers(userList);
      } catch (error) {
        console.error("Error fetching users: ", error);
        alert("Error fetching users from Firebase");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Update trading hours based on trigger
  useEffect(() => {
    if (newUser.trigger === 'טיסה/שבירת כנף') {
      setNewUser(prev => ({ ...prev, tradingHours: '18:30-21:00' }));
    } else if (newUser.trigger === 'AMD') {
      if (newUser.tradingHours !== '9:30-12:30' && newUser.tradingHours !== '15:10-16:00') {
        setNewUser(prev => ({ ...prev, tradingHours: '15:10-16:00' }));
      }
    } else if (newUser.trigger === 'BUTTC/RBTI') {
      setNewUser(prev => ({ ...prev, tradingHours: '21:00-23:00' }));
    } else if (newUser.trigger === 'UT/SP') {
      setNewUser(prev => ({ ...prev, tradingHours: '9:30-12:30' }));
    }
  }, [newUser.trigger]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!newUser.email || !newUser.userName || !newUser.trigger || !newUser.password) return;
    
    try {
      setLoading(true);
      
      // Create user in Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );
      
      const firebaseUID = userCredential.user.uid;
      
      // Save user data to Firestore (without password)
      // Use setDoc with the Firebase Auth UID as the document ID
      await setDoc(doc(db, 'users', firebaseUID), {
        email: newUser.email,
        tradingHours: newUser.tradingHours,
        trigger: newUser.trigger,
        userName: newUser.userName
      });
      
      // Add to local state
      setUsers(prev => [...prev, {
        uid: firebaseUID, // Use Firebase Auth UID
        email: newUser.email,
        tradingHours: newUser.tradingHours,
        trigger: newUser.trigger,
        userName: newUser.userName
      }]);
      
      // Reset form
      setNewUser({
        uid: '',
        email: '',
        password: '',
        tradingHours: '',
        trigger: '',
        userName: ''
      });
      
      alert('משתמש נוצר בהצלחה!');
      
    } catch (error) {
      console.error("Error adding user: ", error);
      
      // Handle specific Firebase Auth errors
      if (error.code === 'auth/email-already-in-use') {
        alert('כתובת האימייל כבר בשימוש');
      } else if (error.code === 'auth/weak-password') {
        alert('הסיסמה חלשה מדי - צריכה להיות לפחות 6 תווים');
      } else if (error.code === 'auth/invalid-email') {
        alert('כתובת אימייל לא תקינה');
      } else {
        alert('שגיאה ביצירת משתמש: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index, uid) => {
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'users', uid));
      setUsers(users.filter((_, i) => i !== index));
    } catch (error) {
      console.error("Error deleting user: ", error);
      alert("Error deleting user from Firebase");
    } finally {
      setLoading(false);
    }
  };

  const triggerOptions = [
    'טיסה/שבירת כנף',
    'AMD',
    'BUTTC/RBTI',
    'UT/SP'
  ];

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #f8fafc, #e2e8f0)',
      padding: '24px',
      direction: 'rtl'
    },
    wrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '32px',
      textAlign: 'center'
    },
    title: {
      fontSize: '36px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px'
    },
    subtitle: {
      color: '#64748b',
      fontSize: '16px'
    },
    formCard: {
      background: 'white',
      borderRadius: '16px',
      padding: '32px',
      marginBottom: '32px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    formTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#334155',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '2px solid #e5e7eb'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px',
      marginBottom: '24px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column'
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#475569',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'all 0.2s',
      outline: 'none',
      background: '#f9fafb'
    },
    inputFocus: {
      border: '2px solid #3b82f6',
      background: 'white'
    },
    select: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'all 0.2s',
      outline: 'none',
      background: '#f9fafb',
      cursor: 'pointer'
    },
    button: {
      background: 'linear-gradient(to right, #3b82f6, #2563eb)',
      color: 'white',
      padding: '12px 32px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)'
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px -1px rgba(59, 130, 246, 0.5)'
    },
    buttonDisabled: {
      background: '#94a3b8',
      cursor: 'not-allowed',
      boxShadow: 'none'
    },
    tableCard: {
      background: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
    },
    tableHeader: {
      background: 'linear-gradient(to right, #f1f5f9, #e2e8f0)',
      padding: '24px 32px',
      borderBottom: '2px solid #e5e7eb'
    },
    tableTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#334155',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      padding: '16px 24px',
      textAlign: 'right',
      fontSize: '12px',
      fontWeight: '600',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      borderBottom: '2px solid #e5e7eb',
      background: '#f8fafc'
    },
    td: {
      padding: '16px 24px',
      fontSize: '14px',
      color: '#334155',
      borderBottom: '1px solid #f1f5f9'
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      background: 'linear-gradient(to right, #dbeafe, #bfdbfe)',
      color: '#1e40af'
    },
    deleteButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      color: '#ef4444',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s'
    },
    deleteButtonHover: {
      color: '#dc2626',
      transform: 'scale(1.05)'
    },
    emptyState: {
      padding: '60px 24px',
      textAlign: 'center',
      color: '#94a3b8'
    },
    loadingState: {
      padding: '60px 24px',
      textAlign: 'center',
      color: '#64748b'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <h1 style={styles.title}>
            <Users size={36} color="#3b82f6" />
            ניהול משתמשים
          </h1>
          <p style={styles.subtitle}>הוסף וערוך משתמשים במערכת המסחר</p>
        </div>
        
        <div style={styles.formCard}>
          <h2 style={styles.formTitle}>הוסף משתמש חדש</h2>
          
          <div style={styles.grid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <Mail size={16} />
                דואר אלקטרוני
              </label>
              <input
                type="email"
                name="email"
                value={newUser.email}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="user@example.com"
                onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <Lock size={16} />
                סיסמה
              </label>
              <input
                type="password"
                name="password"
                value={newUser.password}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="הכנס סיסמה"
                onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <Users size={16} />
                שם משתמש
              </label>
              <input
                type="text"
                name="userName"
                value={newUser.userName}
                onChange={handleInputChange}
                style={styles.input}
                placeholder="הכנס שם משתמש"
                onFocus={(e) => e.target.style.border = '2px solid #3b82f6'}
                onBlur={(e) => e.target.style.border = '2px solid #e5e7eb'}
              />
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <Target size={16} />
                טריגר
              </label>
              <select
                name="trigger"
                value={newUser.trigger}
                onChange={handleInputChange}
                style={styles.select}
              >
                <option value="">בחר טריגר...</option>
                {triggerOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <Clock size={16} />
                שעות מסחר
              </label>
              {newUser.trigger === 'AMD' ? (
                <select
                  name="tradingHours"
                  value={newUser.tradingHours}
                  onChange={handleInputChange}
                  style={styles.select}
                >
                  <option value="9:30-12:30">9:30-12:30</option>
                  <option value="15:10-16:00">15:10-16:00</option>
                </select>
              ) : (
                <input
                  type="text"
                  name="tradingHours"
                  value={newUser.tradingHours}
                  onChange={handleInputChange}
                  style={{...styles.input, background: newUser.trigger ? '#f3f4f6' : '#f9fafb'}}
                  readOnly={newUser.trigger !== ''}
                  placeholder="יוגדר אוטומטית"
                />
              )}
            </div>
          </div>
          
          <button
            onClick={handleSubmit}
            style={loading ? {...styles.button, ...styles.buttonDisabled} : styles.button}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 12px -1px rgba(59, 130, 246, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(59, 130, 246, 0.5)';
              }
            }}
            disabled={loading}
          >
            <Plus size={20} />
            {loading ? 'מוסיף...' : 'הוסף משתמש'}
          </button>
        </div>
        
        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <h2 style={styles.tableTitle}>
              רשימת משתמשים
              <span style={{fontSize: '14px', fontWeight: '400', color: '#64748b'}}>
                ({users.length} משתמשים)
              </span>
            </h2>
          </div>
          
          {loading && users.length === 0 ? (
            <div style={styles.loadingState}>
              <div style={{marginBottom: '12px'}}>טוען משתמשים...</div>
              <div style={{fontSize: '24px', animation: 'spin 1s linear infinite'}}>⚙️</div>
            </div>
          ) : users.length === 0 ? (
            <div style={styles.emptyState}>
              <Users size={48} style={{margin: '0 auto 16px', opacity: 0.3}} />
              <p>אין משתמשים במערכת</p>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>שם משתמש</th>
                  <th style={styles.th}>דואר אלקטרוני</th>
                  <th style={styles.th}>טריגר</th>
                  <th style={styles.th}>שעות מסחר</th>
                  <th style={styles.th}>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.uid}>
                    <td style={{...styles.td, fontWeight: '600'}}>{user.userName}</td>
                    <td style={styles.td}>{user.email}</td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{user.trigger}</span>
                    </td>
                    <td style={styles.td}>{user.tradingHours}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleDelete(index, user.uid)}
                        style={loading ? {...styles.deleteButton, cursor: 'not-allowed', opacity: 0.5} : styles.deleteButton}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.target.style.color = '#dc2626';
                            e.target.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.target.style.color = '#ef4444';
                            e.target.style.transform = 'scale(1)';
                          }
                        }}
                        disabled={loading}
                      >
                        <Trash2 size={16} />
                        מחק
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserForm;